// #336 slice 5, brief §5.3: a progression rule as one sequence per parameter,
// read at (round, set). This file holds the types, the generator (rule →
// the sets of every round) and the diff of a slot against a rule (its
// exceptions, the cells «a mano»). The rule is never stored (§5.1): it is
// recognized from the cells (programProgressionRecognize) and written back
// as cells (programProgressionApply). Keep free of React and any UI import.
import { rpeToRir, type EffortScale } from "./effortScale.js";
import type { CellDraft, IntensityKind, SlotDraft } from "./programEdit.js";
import { amrapOf, isCellAbsent, kindAt, parseFloatLoose, parseRepsToken, VALUELESS_KINDS } from "./programEditBuild.js";
import { cellRepsRange, type RepsRange } from "./programRepsRange.js";
import type { AmrapStop, RoundType } from "./schemas/programV3.js";
import type { UnitsPreference } from "./schemas/profile.js";

/** One parameter across the rounds. The index is the round's place in the
 *  progression: a deload round does not advance it (§5.3, «riprende da dove
 *  era»), and reads the value of the last round that did. */
export type Sequence =
  | { kind: "constant"; value: number }
  /** start + step per round, stopped at `cap` when given (the ramp's «tetto»). */
  | { kind: "linear"; start: number; step: number; cap?: number }
  /** from → to over `rounds` progressing rounds, then held. */
  | { kind: "ramp"; from: number; to: number; rounds: number }
  /** The values round by round, then again from the first (Onde). Indexed
   *  by the round itself: the wave carries its own easy round. */
  | { kind: "cycle"; values: number[] }
  /** Each block restarts its own sequence; the last one runs on. */
  | { kind: "blocks"; blocks: Array<{ rounds: number; seq: Sequence }> };

/** «A soglia» (§5.3): reps climb from min to max by `repsStep`, then the sets
 *  climb to `setsMax` (the triple progression, S1), then the load goes up by
 *  `loadStep` and both start again. */
export type Threshold = {
  repsMin: number;
  repsMax: number;
  repsStep: number;
  setsMax?: number;
  loadStart: number;
  loadStep: number;
};

/** What a test round does: the last set AMRAP, the value moved by `delta`
 *  on every set, on the last one, or on the hardest ones of the round
 *  («le serie di testa»: on a constant row, every set). */
export type TestEffect = {
  amrapLast?: boolean;
  /** #355: where that AMRAP stops. */
  amrapStop?: AmrapStop;
  delta?: number;
  on?: "all" | "last" | "top";
};

/** What a deload round does to the last progressing round: the value times
 *  `factor` then plus `delta`, and `setsDelta` sets (never below one). #356:
 *  a row with no value (history, none) has no value to move: `effort` writes
 *  its deload sets at that effort instead, so «match or beat» stops there. */
export type DeloadEffect = {
  factor?: number;
  delta?: number;
  setsDelta?: number;
  effort?: { axis: "rpe" | "rir"; value: number };
};

export type ProgressionRule = {
  /** The row's intensity axis. */
  axis: IntensityKind;
  /** A countdown set: `reps` carries the seconds. */
  work?: "countdown";
  sets: number;
  reps: Sequence;
  /** null on an axis with no value (none, history). */
  value: Sequence | null;
  /** Added to the round's value, set by set (§5.3 «per serie»). */
  perSet?: { reps?: number[]; value?: number[] };
  /** S3, on every set that is not AMRAP. */
  range?: RepsRange;
  /** Replaces reps, value and the set count. */
  threshold?: Threshold;
  /** Absent: the row has no test round to read an effect from. */
  test?: TestEffect;
  /** Absent: the row has no deload round to read an effect from. */
  deload?: DeloadEffect;
};

/** One generated set. `reps` is seconds on a countdown rule. */
export type RuleSet = {
  reps: number;
  value: number | null;
  amrap: boolean;
  range: RepsRange | null;
  /** #355: the test round's stop rule, on its AMRAP only. */
  amrapStop?: AmrapStop;
  /** #356: the set's axis when it is not the rule's (DeloadEffect.effort). */
  kind?: IntensityKind;
};

/** A cell field that differs from the rule; "sets" is the round's set count. */
export type RuleField = "reps" | "value" | "amrap" | "range" | "sets";
export type RuleException = { week: number; set: number; field: RuleField };

export type GenerateOptions = {
  /** Rounding of a load scaled by a factor, in the cells' unit. Default 2.5. */
  loadQuantum?: number;
};

const typeAt = (types: readonly RoundType[], w: number): RoundType => types[w] ?? "normal";

/** Round `w`'s place in the progression (see Sequence). */
export const progressIndex = (types: readonly RoundType[], w: number): number => {
  let p = 0;
  for (let i = 0; i < w; i += 1) if (typeAt(types, i) !== "deload") p += 1;
  return typeAt(types, w) === "deload" ? Math.max(0, p - 1) : p;
};

const tidy = (x: number): number => Math.round(x * 1000) / 1000;
const roundTo = (x: number, q: number): number => tidy(Math.round(x / q) * q);

/** The value of `seq` at round `w`, before any test or deload effect. */
export const seqAt = (seq: Sequence, w: number, types: readonly RoundType[]): number => {
  if (seq.kind === "blocks") {
    let start = 0;
    for (const [k, b] of seq.blocks.entries()) {
      if (w < start + b.rounds || k === seq.blocks.length - 1) {
        return seqAt(b.seq, w - start, types.slice(start));
      }
      start += b.rounds;
    }
    return 0;
  }
  if (seq.kind === "constant") return seq.value;
  if (seq.kind === "cycle") return seq.values[w % Math.max(1, seq.values.length)] ?? 0;
  const p = progressIndex(types, w);
  if (seq.kind === "ramp") {
    if (seq.rounds <= 1) return seq.to;
    return seq.from + ((seq.to - seq.from) * Math.min(p, seq.rounds - 1)) / (seq.rounds - 1);
  }
  const v = tidy(seq.start + p * seq.step);
  if (seq.cap === undefined) return v;
  return seq.step >= 0 ? Math.min(v, seq.cap) : Math.max(v, seq.cap);
};

/** `seq` moved by `d` everywhere: the next block «un gradino più su». */
export const liftSeq = (seq: Sequence, d: number): Sequence => {
  if (d === 0) return seq;
  switch (seq.kind) {
    case "constant":
      return { kind: "constant", value: tidy(seq.value + d) };
    case "linear":
      return { ...seq, start: tidy(seq.start + d), ...(seq.cap !== undefined ? { cap: tidy(seq.cap + d) } : {}) };
    case "ramp":
      return { ...seq, from: tidy(seq.from + d), to: tidy(seq.to + d) };
    case "cycle":
      return { kind: "cycle", values: seq.values.map((v) => tidy(v + d)) };
    case "blocks":
      return { kind: "blocks", blocks: seq.blocks.map((b) => ({ ...b, seq: liftSeq(b.seq, d) })) };
  }
};

/** An effort axis: RPE or RIR. */
export const isEffortAxis = (axis: IntensityKind): boolean => axis === "rpe" || axis === "rir";

/** Which way «harder» goes on `axis`: up, except on RIR. */
export const harderSign = (axis: IntensityKind): 1 | -1 => (axis === "rir" ? -1 : 1);

/** An RPE value written on `scale` (effortScale.ts does the arithmetic). */
export const effortOn = (rpe: number, scale: EffortScale): number => (scale === "rir" ? rpeToRir(rpe) : rpe);

/** The step of a load the builder offers, and the grain a scaled load rounds
 *  to, in kg. */
export const DEFAULT_LOAD_STEP = 2.5;

/** The builder's load step in the cells' unit: 2.5 kg, or 5 lb. */
export const loadStepFor = (unit: UnitsPreference): number => (unit === "lb" ? 5 : DEFAULT_LOAD_STEP);

/** Where a load starts when the row has neither a load nor a reference (the
 *  mock's 100), in the cells' unit. */
export const DEFAULT_START_LOAD = 100;

/** §5.3 defaults, for a deload a rule does not say: 2 RPE easier on an
 *  effort row, 90 % on a load row, nothing elsewhere. */
export const defaultDeload = (axis: IntensityKind): DeloadEffect =>
  isEffortAxis(axis) ? { delta: -2 * harderSign(axis) }
    : axis === "absolute" || axis === "percent-of-max" ? { factor: 0.9 }
      : {};

/** The grain of a value on `axis`: what a ramp or a factor rounds to. */
const quantumOf = (axis: IntensityKind, opts: GenerateOptions): number =>
  axis === "absolute" ? (opts.loadQuantum ?? DEFAULT_LOAD_STEP) : axis === "none" || axis === "history" ? 1 : 0.5;

const clampValue = (axis: IntensityKind, v: number): number =>
  axis === "rpe" ? Math.min(10, Math.max(1, v))
    : axis === "rir" ? Math.min(10, Math.max(0, v))
      : axis === "percent-of-max" ? Math.min(200, Math.max(0, v))
        : v;

const offsetAt = (offsets: readonly number[] | undefined, i: number): number =>
  offsets && offsets.length > 0 ? (offsets[Math.min(i, offsets.length - 1)] ?? 0) : 0;

/** reps, set count and load of a threshold rule at progress `p`. */
const thresholdAt = (t: Threshold, sets: number, p: number) => {
  const kR = Math.max(1, Math.floor((t.repsMax - t.repsMin) / Math.max(1, t.repsStep)) + 1);
  const kS = t.setsMax !== undefined && t.setsMax > sets ? t.setsMax - sets + 1 : 1;
  return {
    reps: t.repsMin + (p % kR) * t.repsStep,
    sets: sets + (Math.floor(p / kR) % kS),
    load: tidy(t.loadStart + Math.floor(p / (kR * kS)) * t.loadStep),
  };
};

/** The sets of round `w` under `rule`, test and deload effects included. */
const roundOf = (rule: ProgressionRule, types: readonly RoundType[], w: number, opts: GenerateOptions): RuleSet[] => {
  const type = typeAt(types, w);
  const p = progressIndex(types, w);
  const thr = rule.threshold ? thresholdAt(rule.threshold, rule.sets, p) : null;
  let count = thr ? thr.sets : rule.sets;
  if (type === "deload") count += rule.deload?.setsDelta ?? 0;
  count = Math.max(1, count);
  const q = quantumOf(rule.axis, opts);
  const values = Array.from({ length: count }, (_, i): number | null => {
    if (!rule.value && !thr) return null;
    const v = thr ? thr.load : seqAt(rule.value!, w, types) + offsetAt(rule.perSet?.value, i);
    return rule.value?.kind === "ramp" || hasRampBlock(rule.value) ? roundTo(v, q) : v;
  });
  const known = values.filter((v): v is number => v !== null);
  const top = known.length ? (harderSign(rule.axis) > 0 ? Math.max(...known) : Math.min(...known)) : null;
  return values.map((base, i) => {
    let reps = thr ? thr.reps : seqAt(rule.reps, w, types) + offsetAt(rule.perSet?.reps, i);
    if (thr && type === "deload") reps = rule.threshold!.repsMin;
    const effort = type === "deload" && base === null ? rule.deload?.effort : undefined;
    let value = effort ? effort.value : base;
    const last = i === count - 1;
    let amrap = false;
    if (type === "test" && rule.test) {
      amrap = last && rule.test.amrapLast === true;
      const on = rule.test.on ?? "all";
      const hit = on === "all" || (on === "last" && last) || (on === "top" && base === top);
      if (value !== null && rule.test.delta && hit) value += rule.test.delta;
    }
    // An effort deload is the target itself: no factor, no delta on it.
    if (type === "deload" && rule.deload && value !== null && !effort) {
      if (rule.deload.factor !== undefined) value = roundTo(value * rule.deload.factor, q);
      value += rule.deload.delta ?? 0;
    }
    return {
      reps: Math.max(0, Math.round(reps)),
      value: value === null ? null : tidy(clampValue(effort?.axis ?? rule.axis, value)),
      ...(effort ? { kind: effort.axis } : {}),
      amrap,
      range: rule.range && !amrap && !rule.work ? rule.range : null,
      ...(amrap && rule.test?.amrapStop ? { amrapStop: { ...rule.test.amrapStop } } : {}),
    };
  });
};

const hasRampBlock = (seq: Sequence | null): boolean =>
  seq?.kind === "blocks" && seq.blocks.some((b) => b.seq.kind === "ramp");

/** Every round's sets under `rule`, over `rounds` rounds typed by `types`.
 *  Whether the slot runs in a round is not the rule's business: the caller
 *  skips the inactive ones. */
export const generateRounds = (
  rule: ProgressionRule,
  types: readonly RoundType[],
  rounds: number,
  opts: GenerateOptions = {},
): RuleSet[][] => Array.from({ length: rounds }, (_, w) => roundOf(rule, types, w, opts));

/** One set of a slot as numbers. `reps` is the seconds on a timed row. */
export type ReadSet = {
  reps: number | null;
  value: number | null;
  kind: IntensityKind;
  amrap: boolean;
  range: RepsRange | null;
  /** #355: the cell's own stop rule, when it has one. */
  amrapStop?: AmrapStop;
};

/** The sets the slot runs in round `w`, or null when it does not run there. */
export const readRound = (slot: SlotDraft, w: number, timed: boolean): ReadSet[] | null => {
  if (slot.inactiveWeeks[w]) return null;
  const out: ReadSet[] = [];
  for (const row of slot.setRows) {
    if (isCellAbsent(row, w)) break;
    const cell: CellDraft | undefined = row.cells[w];
    if (!cell) break;
    const kind = kindAt(row, w);
    out.push({
      reps: timed ? parseFloatLoose(cell.workDurationSec ?? "") : (parseRepsToken(cell.reps)?.reps ?? null),
      value: VALUELESS_KINDS.has(kind) ? null : parseFloatLoose(cell.intensityValue),
      kind,
      amrap: amrapOf(cell),
      range: cellRepsRange(row, cell),
      ...(cell.amrapStop ? { amrapStop: cell.amrapStop } : {}),
    });
  }
  return out;
};

const same = (a: number | null, b: number | null): boolean =>
  a === null || b === null ? a === b : Math.abs(a - b) < 1e-6;

const sameStop = (a: AmrapStop | undefined, b: AmrapStop | undefined): boolean =>
  a === undefined || b === undefined
    ? a === b
    : a.kind === "rir"
      ? b.kind === "rir" && a.rir === b.rir
      : b.kind === a.kind;

const sameRange = (a: RepsRange | null, b: RepsRange | null): boolean =>
  a === null || b === null ? a === b : a.min === b.min && a.max === b.max;

/** The cells of `slot` that differ from `rule`, round by round, in the
 *  rounds the slot runs in. Empty: the slot follows the rule. */
export const ruleExceptions = (
  slot: SlotDraft,
  rule: ProgressionRule,
  types: readonly RoundType[],
  opts: GenerateOptions = {},
): RuleException[] => {
  const n = slot.inactiveWeeks.length;
  const gen = generateRounds(rule, types, n, opts);
  const out: RuleException[] = [];
  for (let w = 0; w < n; w += 1) {
    const have = readRound(slot, w, rule.work === "countdown");
    const want = gen[w];
    if (!have || !want) continue;
    if (have.length !== want.length) out.push({ week: w, set: 0, field: "sets" });
    for (let i = 0; i < Math.min(have.length, want.length); i += 1) {
      const h = have[i]!;
      const g = want[i]!;
      if (!same(h.reps, g.reps)) out.push({ week: w, set: i, field: "reps" });
      if (h.kind !== (g.kind ?? rule.axis) || !same(h.value, g.value)) out.push({ week: w, set: i, field: "value" });
      // #355: a stop typed by hand on a test AMRAP is the same kind of exception
      // as the AMRAP flag itself, so a rule with a stop does not overwrite it.
      if (h.amrap !== g.amrap || (g.amrapStop !== undefined && h.amrap && !sameStop(h.amrapStop, g.amrapStop))) out.push({ week: w, set: i, field: "amrap" });
      if (!sameRange(h.range, g.range)) out.push({ week: w, set: i, field: "range" });
    }
  }
  return out;
};
